import DOMPurify from "dompurify";
import { marked } from "marked";

/**
 * GFM pipe tables split on `|`. Math like `$P(|Z| > c)$` must not add fake columns.
 * On rows whose trimmed line starts with `|`, replace each unescaped `|` inside `$…$` / `$$…$$`
 * with TeX `\vert` (KaTeX renders the same bar, marked no longer sees a cell delimiter).
 */
export function preprocessMarkdownForPipeTableMathPipes(markdown: string): string {
  return markdown.split("\n").map(escapeUnescapedPipesInMathForTableRow).join("\n");
}

function countBackslashesBefore(s: string, j: number): number {
  let n = 0;
  for (let k = j - 1; k >= 0 && s[k] === "\\"; k--) n++;
  return n;
}

function isUnescapedDollar(s: string, j: number): boolean {
  return countBackslashesBefore(s, j) % 2 === 0;
}

function findNextUnescapedDoubleDollar(s: string, from: number): number {
  for (let j = from; j < s.length - 1; j++) {
    if (s[j] === "$" && s[j + 1] === "$" && isUnescapedDollar(s, j)) return j;
  }
  return -1;
}

function findFirstUnescapedDollarFrom(s: string, from: number): number {
  for (let j = from; j < s.length; j++) {
    if (s[j] === "$" && isUnescapedDollar(s, j)) return j;
  }
  return -1;
}

function escapeUnescapedPipesInSubstring(s: string): string {
  let r = "";
  for (let j = 0; j < s.length; j++) {
    if (s[j] === "|" && countBackslashesBefore(s, j) % 2 === 0) {
      if (j > 0 && /[0-9A-Za-z]/.test(s[j - 1]!)) r += " ";
      r += "\\vert";
      const next = s[j + 1];
      if (next !== undefined && /[0-9A-Za-z]/.test(next)) r += " ";
    } else {
      r += s[j]!;
    }
  }
  return r;
}

function escapeUnescapedPipesInMathForTableRow(line: string): string {
  if (!line.trimStart().startsWith("|")) return line;
  let out = "";
  let i = 0;
  while (i < line.length) {
    if (line.startsWith("$$", i)) {
      const close = findNextUnescapedDoubleDollar(line, i + 2);
      if (close < 0) {
        out += line.slice(i);
        break;
      }
      out += line.slice(i, i + 2);
      out += escapeUnescapedPipesInSubstring(line.slice(i + 2, close));
      out += line.slice(close, close + 2);
      i = close + 2;
      continue;
    }
    if (line[i] === "$" && isUnescapedDollar(line, i)) {
      const close = findFirstUnescapedDollarFrom(line, i + 1);
      if (close < 0) {
        out += line.slice(i);
        break;
      }
      out += line[i]!;
      out += escapeUnescapedPipesInSubstring(line.slice(i + 1, close));
      out += line[close]!;
      i = close + 1;
      continue;
    }
    out += line[i]!;
    i++;
  }
  return out;
}

export type MarkdownToSafeHtmlOptions = {
  /** When true, single newlines become `<br>` (GitHub-style); default follows CommonMark paragraphs. */
  breaks?: boolean;
};

/** GFM markdown → sanitized HTML for the preview pane. */
export function markdownToSafeHtml(markdown: string, opts?: MarkdownToSafeHtmlOptions): string {
  const md = preprocessMarkdownForPipeTableMathPipes(markdown);
  const raw = marked.parse(md, { async: false, breaks: opts?.breaks ?? false }) as string;
  return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
}

/**
 * GFM markdown → sanitized HTML with `data-source-line="N"` (0-based) on every top-level block
 * element. Enables bidirectional scroll sync between the source editor and the preview pane.
 * DOMPurify keeps `data-*` attributes by default (`ALLOW_DATA_ATTR` is `true`).
 */
export function markdownToSafeHtmlAnnotated(markdown: string, opts?: MarkdownToSafeHtmlOptions): string {
  const md = preprocessMarkdownForPipeTableMathPipes(markdown);
  // Step 1: lex to get block tokens with their raw source text.
  const tokens = marked.lexer(md);

  // Step 2: walk top-level tokens to record the 0-based starting line for each non-space block.
  const blockSourceLines: number[] = [];
  let currentLine = 0;
  for (const token of tokens) {
    const raw = (token as { raw?: string }).raw ?? "";
    if (token.type !== "space") {
      blockSourceLines.push(currentLine);
    }
    for (let i = 0; i < raw.length; i++) {
      if (raw.charCodeAt(i) === 10 /* \n */) currentLine++;
    }
  }

  // Step 3: render to HTML string using the standard pipeline.
  const rawHtml = marked.parse(md, { async: false, breaks: opts?.breaks ?? false }) as string;

  // Step 4: parse into a temporary DOM node and stamp each direct child with its source line.
  // This is browser-only code (called exclusively from AppShell which runs in a browser).
  if (typeof document === "undefined") {
    // Fallback for non-browser environments (tests, SSR): return unannotated HTML.
    return DOMPurify.sanitize(rawHtml, { USE_PROFILES: { html: true } });
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = rawHtml;
  let idx = 0;
  for (const child of Array.from(tmp.children)) {
    if (idx < blockSourceLines.length) {
      child.setAttribute("data-source-line", String(blockSourceLines[idx++]));
    }
  }

  // Step 5: sanitize — DOMPurify's ALLOW_DATA_ATTR defaults to true so data-source-line survives.
  return DOMPurify.sanitize(tmp.innerHTML, { USE_PROFILES: { html: true } });
}

/**
 * Max prose: allow embedded `data:` images/PDFs, `data:video/…;base64,…` on `<video src>`,
 * hex `npp-max-video:1;` / `npp-max-audio:1;` links for toolbar recordings, and `<video controls …>`.
 */
const MAX_PROSE_PURIFY = {
  USE_PROFILES: { html: true } as const,
  ADD_TAGS: ["video", "audio"],
  ADD_ATTR: ["controls", "playsinline", "preload", "class", "title", "src"],
  ALLOWED_URI_REGEXP:
    /^(?:(?:f|ht)tps?|mailto|tel|data:image\/[-+.\w]+;base64,|data:video\/[-+.\w]+;base64,|data:application\/pdf(?:;npp-page=\d{1,6})?;base64,[A-Za-z0-9+/=]+(?:#page=\d{1,6})?|npp-max-html:1;[0-9a-f]+|npp-max-video:1;[0-9a-f]+|npp-max-audio:1;[0-9a-f]+)/i,
};

/**
 * Markdown for Max **prose** segments only: single newlines become `<br>` (CommonMark `breaks`),
 * so `a\\nb` matches the editor’s two lines instead of collapsing to `a b`.
 */
export function markdownToSafeHtmlMaxProse(markdown: string): string {
  const md = preprocessMarkdownForPipeTableMathPipes(markdown);
  const raw = marked.parse(md, { async: false, breaks: true }) as string;
  return DOMPurify.sanitize(raw, MAX_PROSE_PURIFY);
}
